import { Severity } from 'Contexts/RestaurantContext';
import { WaitingListEntryDocument } from 'types/shared/documents';
import { RequestStatus } from 'types/shared/helper/reservations';
import React, { useMemo } from 'react';
import { EditReservation } from 'types/reservations';
import { db, FieldValue } from 'utils/firebase';
import useAuth from './useAuth';
import useCollection from './useCollection';
import useToast from './useToast';
import { useRouter } from './useRouter';
import useRestaurant from './useRestaurant';

export enum WaitingListActionType {
  FIND_TABLE = 'findTable',
  CANCEL = 'cancel',
  ACCEPT = 'accept',
  HOST_COMMENT = 'hostComment',
  UN_CANCEL = 'unCancel',
}

const useWaitinglist = (
  restaurantId: string | null,
  date: string,
  seteditReservation: React.Dispatch<
    React.SetStateAction<EditReservation | null>
  >,
  upcomingEntries: WaitingListEntryDocument[] = []
) => {
  const [waitinglistEntryDocs] = useCollection<WaitingListEntryDocument>(
    'waitinglistEntries',
    {
      filter: [
        ['restaurant', '==', restaurantId],
        ['date', '==', date],
      ],
    }
  );

  const navigation = useRouter();

  const { experimentalFlags } = useRestaurant();

  const sendConfirmationEmailDefault =
    !experimentalFlags?.data?.confirmationEmailOffByDefault;

  console.log({ waitinglistEntryDocs });

  const { uid } = useAuth();

  const toast = useToast();

  const waitinglistEntries = useMemo(
    () =>
      waitinglistEntryDocs.data
        .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0))
        .map((entry, index) => ({ ...entry, index })),
    [waitinglistEntryDocs]
  );

  const handleWaitinglistAction = (
    id: string,
    type: WaitingListActionType,
    payload?: any
  ) => {
    // The mobile list shows open requests of other days as well.
    const entry =
      waitinglistEntries.find((e) => e.id === id) ??
      upcomingEntries.find((e) => e.id === id);

    const changedMeanwhile = () =>
      toast(
        'This request was changed in the meantime.',
        Severity.INFO,
        'reservations'
      );

    // Detail views can show a request that was handled elsewhere meanwhile.
    if (
      !entry ||
      (type === WaitingListActionType.FIND_TABLE &&
        entry.status !== RequestStatus.PENDING)
    ) {
      if (type !== WaitingListActionType.ACCEPT) changedMeanwhile();
      return;
    }

    const ref = waitinglistEntryDocs.ref.doc(id);

    const updateInfos = { updatedAt: Date.now(), updatedBy: uid };

    // Status changes and their undo re-read the status in a transaction, a
    // dialog, the listener or the toast can still hold an old one.
    const changeStatus = (
      allowedFrom: RequestStatus[],
      update: Record<string, any>,
      message: string,
      undo: (prev: WaitingListEntryDocument) => Record<string, any>
    ) =>
      db
        .runTransaction(async (tx) => {
          const prev = (await tx.get(ref)).data() as
            | WaitingListEntryDocument
            | undefined;

          // An accepted request has its reservation before its own status
          // turns to success.
          const accepted =
            update.status === RequestStatus.CANCELED &&
            (await tx.get(db.collection('requestsV03').doc(id))).exists;

          if (!prev || !allowedFrom.includes(prev.status) || accepted)
            return null;

          tx.update(ref, { ...updateInfos, ...update });

          return prev;
        })
        .then((prev) => {
          if (!prev) return changedMeanwhile();

          toast(message, Severity.SUCCESS, 'reservations', () =>
            db
              .runTransaction(async (tx) => {
                if ((await tx.get(ref)).data()?.status !== update.status)
                  return false;

                tx.update(ref, {
                  updatedAt: Date.now(),
                  updatedBy: uid,
                  ...undo(prev),
                });

                return true;
              })
              .then((done) => {
                if (!done) changedMeanwhile();
              })
              .catch((err: any) => {
                toast(err.message, Severity.ERROR, 'reservations');
              })
          );
        })
        .catch((err: any) => {
          toast(err.message, Severity.ERROR, 'reservations');
        });

    // if (!window.location?.href.includes('tablePlan')) {
    //   toast(
    //     'Bitte wechsle in den Tischplan um diese Funktion zu nutzen',
    //     Severity.INFO,
    //     'reservations'
    //   );
    //   return;
    // }

    switch (type) {
      case WaitingListActionType.ACCEPT: {
        // @TODO

        return;
      }
      case WaitingListActionType.UN_CANCEL: {
        changeStatus(
          [RequestStatus.CANCELED, RequestStatus.FAILED],
          {
            canceledAt: FieldValue.delete(),
            canceledBy: FieldValue.delete(),
            status: RequestStatus.PENDING,
          },
          'Wartelisten Eintrag wurde wiederhergestellt',
          (prev) => ({
            status: prev.status,
            canceledAt: prev.canceledAt ?? FieldValue.delete(),
            canceledBy: prev.canceledBy ?? FieldValue.delete(),
          })
        );

        return;
      }
      case WaitingListActionType.CANCEL: {
        changeStatus(
          [RequestStatus.PENDING, RequestStatus.WAITING],
          {
            canceledAt: updateInfos.updatedAt,
            canceledBy: uid,
            status: RequestStatus.CANCELED,
          },
          'Wartelisten Eintrag wurde abgesagt',
          (prev) => ({
            canceledAt: FieldValue.delete(),
            canceledBy: FieldValue.delete(),
            status: prev.status,
          })
        );

        return;
      }
      case WaitingListActionType.FIND_TABLE: {
        let { id, date, time, occassion, guests, guest } = entry;

        const isTimeTable = window.location.pathname.endsWith('timeTable');

        if (isTimeTable) {
          navigation.push('tablePlan');
        }

        seteditReservation({
          date,
          time,
          tables: [],
          tableStr: null,
          reservationId: id,
          occassion,
          guests,
          guest,
          walkin: false,
          timeSlot: null,
          waitinglistEntry: entry,
          sendConfirmationEmail: sendConfirmationEmailDefault,
          originalUrl: isTimeTable ? 'timeTable?tab=waitinglist' : 'tablePlan',
        });
        return;
      }
      case WaitingListActionType.HOST_COMMENT: {
        ref
          .update({
            ...updateInfos,
            'guest.hostComment': payload,
          })
          .then(() => {
            toast(
              'Kommentar gespeichert',
              Severity.SUCCESS,
              'reservations',
              () => {
                ref.update({
                  ...updateInfos,
                  'guest.hostComment': entry.guest.hostComment || '',
                });
              }
            );
          })
          .catch((err: any) => {
            toast(err.message, Severity.ERROR, 'reservations');
          });

        return;
      }
      default:
        break;
    }
  };

  return {
    waitinglistEntries,
    handleWaitinglistAction,
  };
};

export default useWaitinglist;
